import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { TRPCClientError } from '@trpc/client'

import { isMutationErrorHandled, showError } from '@/utils/toast'

const NON_RETRYABLE_TRPC_CODES = new Set([
  'BAD_REQUEST',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'METHOD_NOT_SUPPORTED',
  'CONFLICT',
  'PRECONDITION_FAILED',
  'PAYLOAD_TOO_LARGE',
  'UNSUPPORTED_MEDIA_TYPE',
  'UNPROCESSABLE_CONTENT',
])

const MAX_RETRIES = 3

function getErrorMessage(error: Error): string {
  return error.message || 'Unexpected error'
}

export function shouldRetry(failureCount: number, error: Error): boolean {
  if (failureCount >= MAX_RETRIES) return false
  if (error instanceof TRPCClientError) {
    const code = (error.data as { code?: unknown } | null | undefined)?.code
    if (typeof code === 'string' && NON_RETRYABLE_TRPC_CODES.has(code))
      return false
  }
  return true
}

/**
 * The tRPC error code carried by a *client* error, or `undefined`.
 *
 * The only correct way to read it. A client-side query/mutation error is a
 * `TRPCClientError`, which is **not** an instance of `@trpc/server`'s
 * `TRPCError` — so `error instanceof TRPCError` in browser code is always
 * false. `useView` checked exactly that, which made its `NOT_FOUND` redirect
 * dead code: a deleted view never redirected and always showed the misleading
 * "check your internet connection" toast instead (KBE-770).
 */
export function getTrpcErrorCode(error: unknown): string | undefined {
  if (!(error instanceof TRPCClientError)) return undefined
  const code = (error.data as { code?: unknown } | null | undefined)?.code
  return typeof code === 'string' ? code : undefined
}

/**
 * Retries a `TIMEOUT` and refuses everything else.
 *
 * `TIMEOUT` means the request never reached its procedure. It is raised in a
 * single place — `resolveContextUser` in `src/server/context.ts`, which runs
 * inside `createContext`, **before any procedure body** — when the GoTrue
 * session check could not complete. So it says nothing about the request's
 * content, and retrying it can neither duplicate a write nor change an answer.
 *
 * That exactness is what makes this usable in the two places that must
 * otherwise stay single-shot:
 *
 * - **Mutations.** Most of ours are not idempotent, so they are `retry: false`
 *   by default. But queries already treat `TIMEOUT` as retryable (KBE-767),
 *   which made a GoTrue blip invisible on a read and *fatal* on a write: the
 *   mutation answers 408 "Could not verify the session:
 *   AuthRetryableFetchError", the write never happens, and all the researcher
 *   gets is an "Action failed" toast. `withTrpcMutation` caught
 *   `assumptionSet.addAssumptionSet` answering exactly that (KBE-770).
 * - **`bayesian.diagnose`.** It sets `retry` to skip the backoff so a genuine
 *   inference error reaches the UI promptly (the "Unobserve state" alert). A
 *   `TIMEOUT` is not an inference error, so retrying only that keeps the prompt
 *   error path intact while removing the permanent page-blanking.
 *
 * GoTrue rate-limits, so this is a researcher-facing bug too, not only a CI
 * one: several people editing at once can produce the same blip. The server
 * already retries the auth check once (`checkAuthWithRetry`); this covers the
 * case where that was not enough.
 *
 * Before reusing this anywhere else, re-check the premise by grepping for
 * `code: 'TIMEOUT'` — the safety argument collapses the moment a procedure
 * body can raise it.
 */
// `error` is `unknown` rather than `Error` so the same predicate satisfies both
// React Query's `DefaultError` signature and tRPC's `TRPCClientErrorLike`; the
// body only ever reads a tRPC error's `data.code`.
export function shouldRetryOnlyOnSessionTimeout(
  failureCount: number,
  error: unknown,
): boolean {
  if (failureCount >= MAX_RETRIES) return false
  return getTrpcErrorCode(error) === 'TIMEOUT'
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      retry: shouldRetry,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
    },
    mutations: {
      retry: shouldRetryOnlyOnSessionTimeout,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      const meta = query.meta as
        { onError?: (error: Error) => void; silent?: boolean } | undefined
      if (meta?.onError && typeof meta.onError === 'function') {
        meta.onError(error)
        return
      }
      if (meta?.silent) return
      // Pass the Error as a top-level argument (not nested in an object) so
      // PostHog's capture_console_errors and the browser console extract its
      // real message + stack instead of logging the wrapper as
      // "[object Object]". Context goes in a separate structured argument.
      console.error('Query failed', error, { queryKey: query.queryKey })
      showError('Failed to load data', {
        description: getErrorMessage(error),
      })
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      const meta = mutation.meta as
        { silent?: boolean; suppressGlobalToast?: boolean } | undefined
      const hasLocalOnError = typeof mutation.options.onError === 'function'
      // `isMutationErrorHandled` covers everything wrapped in
      // `mutationWithFeedback` / `mutationWithErrorFeedback` — those
      // already showed a tailored toast, so the global fallback would
      // be a duplicate (the "Action failed" toast on top of the
      // feature-specific one). The explicit `suppressGlobalToast` /
      // `silent` meta flags remain available for callers that don't
      // route through those helpers.
      if (
        hasLocalOnError ||
        meta?.silent ||
        meta?.suppressGlobalToast ||
        isMutationErrorHandled(error)
      )
        return
      // See the Query failed note above: Error passed top-level so PostHog /
      // the browser capture its message + stack, not "[object Object]".
      console.error('Mutation failed', error, {
        mutationKey: mutation.options.mutationKey,
      })
      showError('Action failed', {
        description: getErrorMessage(error),
      })
    },
  }),
})

export default queryClient
